import { Chip, useTheme } from "@mui/material";
import { color, type SeverityKey } from "../../theme/tokens/color.js";
import { alphaOver } from "../../theme/statusChip.js";

export interface SeverityChipProps {
  /**
   * Severity story (lyse 8.0.0 / UX Q6) — `null` = "not graded" (the wire's
   * nullable derived severity). Renders a neutral chip labelled
   * "Not graded"; never a fabricated grade.
   */
  severity: SeverityKey | null;
  label?: string;
  /**
   * Class-model grade colour (e.g. the wire's `Defect.classColor`) — when
   * the inspector UI shows the class-model's own grade labels (UX Q6), the
   * model's colour wins over the derived-severity palette.
   */
  colorOverride?: string;
  /** Accessible-name override (e.g. "Severity grade 3"). */
  ariaLabel?: string;
  onDelete?: () => void;
  /** Smaller chip variant for dense grids. */
  dense?: boolean;
}

const LABELS: Record<SeverityKey, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
  info: "Info",
};

/** UX Q6 — the one user-facing label for a null (ungraded) severity. */
const NOT_GRADED_LABEL = "Not graded";

/**
 * D83's grade scale, rendered as a TONAL chip with a leading STRIP.
 *
 * It used to be a saturated fill behind pure white text — the pattern D81
 * measured and rejected for status. The measurement generalises: a saturated
 * fill only carries white text where the fill is dark enough, and a five-tier
 * ramp spans light amber to deep red, so one foreground cannot serve all five.
 * The old code knew this and worked around it by pinning `#FFFFFF` instead of
 * `text.inverse`, buying `4.73:1` on the worst tier — a third of a point of
 * headroom, on a value nobody would re-measure after the next palette change.
 *
 * Worse, `colorOverride` accepts an ARBITRARY tenant-supplied class colour.
 * White on an unknown hue is a coin flip; the old code had no answer for a
 * light one at all.
 *
 * So severity takes the same tonal recipe as status — hue in the fill and the
 * keyline, label neutral — which is safe for any hue including one the tenant
 * chose. What keeps a severity chip and a status chip apart is FORM: status
 * carries a round DOT, severity a leading STRIP. Two shapes, one hue
 * vocabulary, and D26 satisfied structurally in both.
 */
export function SeverityChip({
  severity,
  label,
  colorOverride,
  ariaLabel,
  onDelete,
  dense = false,
}: SeverityChipProps) {
  const theme = useTheme();
  const mode = theme.palette.mode === "dark" ? "dark" : "light";
  const palette = color[mode];

  // `null` is not a grade and must not borrow one. `--status-neutral` means
  // exactly "inert · not started", which is what an ungraded finding is.
  const hue =
    colorOverride ??
    (severity === null ? palette.status.neutral : palette.severity[severity]);

  const ground = palette.surface.default;
  const effectiveLabel =
    label ?? (severity === null ? NOT_GRADED_LABEL : LABELS[severity]);

  return (
    <Chip
      label={effectiveLabel}
      size={dense ? "small" : "medium"}
      {...(onDelete ? { onDelete } : {})}
      sx={{
        backgroundColor: alphaOver(hue, palette.chip.fillAlpha, ground),
        color: palette.chipLabel,
        fontWeight: 600,
        border: `1px solid ${alphaOver(hue, palette.chip.keylineAlpha, ground)}`,
        // The strip — severity's own shape. Inset so the chip's radius does not
        // clip it into a crescent.
        position: "relative",
        overflow: "hidden",
        paddingInlineStart: "10px",
        "&::before": {
          content: '""',
          position: "absolute",
          insetBlock: 0,
          insetInlineStart: 0,
          width: "4px",
          backgroundColor: hue,
        },
        "& .MuiChip-label": { color: palette.chipLabel },
        "& .MuiChip-deleteIcon": {
          color: palette.chipLabel,
          opacity: 0.8,
          "&:hover": { opacity: 1, color: palette.chipLabel },
        },
      }}
      data-severity={severity ?? "not-graded"}
      aria-label={ariaLabel ?? `${effectiveLabel} severity`}
    />
  );
}
